(() => {
  const REFRESH_EVENT = 'cart-shipping-guide:refresh';
  const CART_EVENTS = [
    REFRESH_EVENT,
    'cart:updated',
    'cart:refresh',
    'cart-drawer:refresh',
    'theme:cart:change',
    'shopify:cart:updated',
    'cart:discount:updated',
    'discount:updated',
    'discount:applied',
    'ajaxProduct:added',
    'product:added'
  ];

  const debounce = (callback, delay = 100) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => callback(...args), delay);
    };
  };

  const safeSelector = (selector) => {
    try { return selector ? document.querySelector(selector) : null; }
    catch (error) {
      console.warn('Cart Shipping Guide: invalid mount selector.', selector, error);
      return null;
    }
  };

  const stripHTML = (value) => {
    const node = document.createElement('div');
    node.innerHTML = value;
    return node.textContent || '';
  };

  const formatNumber = (value, precision, thousands, decimal) => {
    const number = Number(value || 0);
    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.length > 1 ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
  };

  const formatMoney = (cents, format, currency) => {
    if (window.Shopify?.formatMoney) {
      try { return stripHTML(window.Shopify.formatMoney(cents, format)); }
      catch (_) {}
    }

    const template = format || '${{amount}}';
    const placeholder = template.match(/{{\s*(\w+)\s*}}/);
    if (placeholder) {
      const formats = {
        amount: [2, ',', '.'],
        amount_no_decimals: [0, ',', '.'],
        amount_with_comma_separator: [2, '.', ','],
        amount_no_decimals_with_comma_separator: [0, '.', ','],
        amount_with_apostrophe_separator: [2, "'", '.']
      };
      const options = formats[placeholder[1]] || formats.amount;
      return stripHTML(template.replace(placeholder[0], formatNumber(cents, ...options)));
    }

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency', currency: currency || 'USD'
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return String(Number(cents || 0) / 100);
    }
  };

  const replaceTokens = (template, values) => {
    let output = String(template || '');
    Object.entries(values).forEach(([key, value]) => {
      output = output
        .split(`{{ ${key} }}`).join(value)
        .split(`{{${key}}}`).join(value)
        .split(`[${key}]`).join(value);
    });
    return output;
  };

  class CartShippingGuide extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      const checkoutLink = this.querySelector('[data-csg-checkout]');
      checkoutLink?.addEventListener('click', (event) => {
        if (!window.Shopify?.designMode) return;
        event.preventDefault();
        const language = (document.documentElement.lang || '').toLowerCase();
        const message = language.startsWith('zh')
          ? 'Shopify 主题编辑器不支持打开真实结账页。请点击主题右上角的预览，在独立店铺页面中测试结账按钮。'
          : 'Shopify cannot open the real checkout inside the theme editor. Open the theme preview in a separate storefront tab to test checkout.';
        window.alert(message);
      });
      this.querySelectorAll('[data-csg-add]').forEach((button) => {
        button.addEventListener('click', () => this.addTopUpProduct(button));
      });
      this.querySelector('[data-csg-back]')?.addEventListener('click', () => {
        const fallback = this.querySelector('[data-csg-back]')?.dataset.fallbackUrl || '/collections/all';
        if (document.referrer && document.referrer !== window.location.href) window.history.back();
        else window.location.href = fallback;
      });
    }

    async addTopUpProduct(button) {
      if (button.disabled) return;
      const variantId = Number(button.dataset.variantId);
      if (!variantId) return;
      const originalText = button.dataset.addText || button.textContent;
      const status = button.parentElement?.querySelector('[data-csg-add-status]');
      button.disabled = true;
      button.classList.remove('has-error');
      button.textContent = button.dataset.addingText || originalText;

      try {
        const cartUi = document.querySelector('cart-drawer, cart-notification');
        const sectionIds = typeof cartUi?.getSectionsToRender === 'function'
          ? cartUi.getSectionsToRender().map((section) => section.id).filter(Boolean)
          : [];
        const root = window.Shopify?.routes?.root || '/';
        const payload = { items: [{ id: variantId, quantity: 1 }] };
        if (sectionIds.length) {
          payload.sections = sectionIds;
          payload.sections_url = window.location.pathname;
        }
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(payload)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || `HTTP ${response.status}`);

        button.textContent = button.dataset.addedText || originalText;
        if (status) status.textContent = button.dataset.addedText || originalText;
        if (result.sections && typeof cartUi?.renderContents === 'function') cartUi.renderContents(result);
        document.dispatchEvent(new CustomEvent('product:added', { detail: { product: result, source: 'cart-shipping-guide' } }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'cart-shipping-guide' } }));
        document.dispatchEvent(new CustomEvent(REFRESH_EVENT));
      } catch (error) {
        console.warn('Cart Shipping Guide: product add failed.', error);
        button.disabled = false;
        button.classList.add('has-error');
        button.textContent = button.dataset.errorText || originalText;
        if (status) status.textContent = button.dataset.errorText || originalText;
        window.setTimeout(() => {
          button.classList.remove('has-error');
          button.textContent = originalText;
        }, 2500);
      }
    }

    update(cart) {
      if (!cart || typeof cart !== 'object') return this.showFallback();
      const count = Number(cart.item_count || 0);
      const threshold = Math.max(0, Number(this.dataset.threshold || 0));
      const total = this.dataset.calculationBasis === 'before_discount'
        ? Number(cart.original_total_price ?? cart.items_subtotal_price ?? cart.total_price ?? 0)
        : Number(cart.total_price ?? 0);
      const remaining = Math.max(0, threshold - total);
      const unlocked = count > 0 && threshold > 0 && total >= threshold;
      const progress = threshold > 0 ? Math.min(100, Math.max(0, total / threshold * 100)) : 0;
      const money = (value) => formatMoney(value, this.dataset.moneyFormat, cart.currency);
      const tokens = {
        remaining_amount: money(remaining),
        threshold: money(threshold),
        cart_total: money(total)
      };

      if (this.dataset.shippingMode === 'threshold') {
        const message = count === 0
          ? this.dataset.emptyText
          : unlocked ? this.dataset.unlockedText : this.dataset.lockedText;
        const messageNode = this.querySelector('[data-csg-message]');
        if (messageNode) messageNode.textContent = replaceTokens(message, tokens);

        const progressWrap = this.querySelector('[data-csg-progress-wrap]');
        const progressTrack = this.querySelector('[data-csg-progress-track]');
        const progressBar = this.querySelector('[data-csg-progress-bar]');
        const progressValue = this.querySelector('[data-csg-progress-value]');
        if (progressWrap) progressWrap.hidden = count === 0 || threshold <= 0;
        if (progressTrack) progressTrack.setAttribute('aria-valuenow', String(Math.round(progress)));
        if (progressBar) progressBar.style.width = `${progress}%`;
        if (progressValue) progressValue.textContent = `${Math.round(progress)}%`;

        const topUp = this.querySelector('[data-csg-top-up]');
        if (topUp) {
          const cartProductIds = new Set((cart.items || []).map((item) => String(item.product_id)));
          const productCards = [...topUp.querySelectorAll('[data-csg-product-card]')];
          productCards.forEach((card) => {
            card.hidden = cartProductIds.has(String(card.dataset.productId));
          });
          const hasVisibleProducts = productCards.length === 0 || productCards.some((card) => !card.hidden);
          topUp.hidden = count === 0 || unlocked || !hasVisibleProducts;
        }
      }

      if (this.dataset.spendSaveEnabled === 'true') {
        // A hyphen followed by a digit is preserved in DOMStringMap keys.
        // Read the corrected tier2/tier3 names first and keep the old keys as
        // a fallback so cached section markup continues to work after deploy.
        const tierData = (name, legacyName) => this.dataset[name] ?? this.dataset[legacyName];
        const promoTotal = this.dataset.spendSaveBasis === 'original'
          ? Number(cart.original_total_price ?? cart.items_subtotal_price ?? cart.total_price ?? 0)
          : Number(cart.items_subtotal_price ?? cart.original_total_price ?? cart.total_price ?? 0);
        const tierCount = Math.min(3, Math.max(1, Number(this.dataset.spendSaveTierCount || 1)));
        const tiers = [
          {
            threshold: Math.max(0, Number(this.dataset.spendSaveThreshold || 0)),
            type: this.dataset.spendSaveDiscountType,
            value: Number(this.dataset.spendSaveDiscountValue || 0)
          },
          {
            threshold: Math.max(0, Number(tierData('spendSaveTier2Threshold', 'spendSaveTier-2Threshold') || 0)),
            type: tierData('spendSaveTier2DiscountType', 'spendSaveTier-2DiscountType'),
            value: Number(tierData('spendSaveTier2DiscountValue', 'spendSaveTier-2DiscountValue') || 0)
          },
          {
            threshold: Math.max(0, Number(tierData('spendSaveTier3Threshold', 'spendSaveTier-3Threshold') || 0)),
            type: tierData('spendSaveTier3DiscountType', 'spendSaveTier-3DiscountType'),
            value: Number(tierData('spendSaveTier3DiscountValue', 'spendSaveTier-3DiscountValue') || 0)
          }
        ].slice(0, tierCount);
        const discountText = (tier) => tier.type === 'percent'
          ? `${tier.value}%`
          : money(Math.round(tier.value * 100));
        const finalTier = tiers[tiers.length - 1];
        const nextTier = tiers.find((tier) => promoTotal < tier.threshold) || finalTier;
        const promoUnlocked = count > 0 && finalTier.threshold > 0 && promoTotal >= finalTier.threshold;
        const promoRemaining = Math.max(0, nextTier.threshold - promoTotal);
        const promoTokens = {
          remaining_amount: money(promoRemaining),
          threshold: money(nextTier.threshold),
          discount_value: discountText(nextTier),
          cart_total: money(promoTotal)
        };
        const promoTemplate = count === 0
          ? this.dataset.spendSaveEmptyText
          : promoUnlocked ? this.dataset.spendSaveUnlockedText : this.dataset.spendSaveLockedText;
        const promoMessage = this.querySelector('[data-csg-spend-save-message]');
        if (promoMessage) promoMessage.textContent = replaceTokens(promoTemplate, promoTokens);
        const promoProgressWrap = this.querySelector('[data-csg-spend-save-progress]');
        if (promoProgressWrap) promoProgressWrap.hidden = count === 0 || !tiers.length;
        this.querySelectorAll('[data-csg-tier]').forEach((tierNode, index) => {
          const tier = tiers[index];
          if (!tier) {
            tierNode.hidden = true;
            return;
          }
          tierNode.hidden = false;
          const previousThreshold = index === 0 ? 0 : tiers[index - 1].threshold;
          const range = tier.threshold - previousThreshold;
          let progress = 0;
          if (range > 0 && promoTotal > previousThreshold) {
            progress = Math.min(100, Math.max(0, (promoTotal - previousThreshold) / range * 100));
          } else if (tier.threshold > 0 && promoTotal >= tier.threshold) {
            progress = 100;
          }
          const track = tierNode.querySelector('[data-csg-tier-track]');
          const bar = tierNode.querySelector('[data-csg-tier-bar]');
          if (track) track.setAttribute('aria-valuenow', String(Math.round(progress)));
          if (bar) bar.style.width = `${progress}%`;
          tierNode.classList.toggle('is-unlocked', promoTotal >= tier.threshold && tier.threshold > 0);
          tierNode.classList.toggle('is-current', promoTotal < tier.threshold && nextTier === tier);
        });
        this.classList.toggle('is-promo-unlocked', promoUnlocked);
      }

      const summary = this.querySelector('[data-csg-order-summary]');
      if (summary) {
        const originalTotal = Number(cart.original_total_price ?? cart.items_subtotal_price ?? cart.total_price ?? 0);
        const discountedTotal = Number(cart.total_price ?? 0);
        const discountAmount = Math.max(
          0,
          Number(cart.total_discount || 0),
          originalTotal - discountedTotal
        );
        const originalNode = summary.querySelector('[data-csg-summary-original]');
        const discountRow = summary.querySelector('[data-csg-summary-discount-row]');
        const discountNode = summary.querySelector('[data-csg-summary-discount]');
        const totalNode = summary.querySelector('[data-csg-summary-total]');
        if (originalNode) {
          originalNode.textContent = money(originalTotal);
          originalNode.classList.toggle('csg-order-summary__value--original', discountAmount > 0);
        }
        if (discountRow) discountRow.hidden = discountAmount <= 0;
        if (discountNode) discountNode.textContent = `−${money(discountAmount)}`;
        if (totalNode) totalNode.textContent = money(discountedTotal);
        summary.hidden = count === 0;
      }

      this.classList.toggle('is-unlocked', unlocked);
      this.classList.toggle('is-empty', count === 0);
      const actions = this.querySelector('[data-csg-actions]');
      const emptyAction = this.querySelector('[data-csg-empty-action]');
      if (actions) actions.hidden = count === 0;
      if (emptyAction) emptyAction.hidden = count > 0;
      const status = this.querySelector('[data-csg-status]');
      const visibleMessage = this.querySelector('[data-csg-message]')?.textContent || '';
      if (status && status.textContent !== visibleMessage) status.textContent = visibleMessage;
    }

    showFallback() {
      const message = this.querySelector('[data-csg-message]');
      if (message && this.dataset.fallbackText) message.textContent = this.dataset.fallbackText;
      this.querySelector('[data-csg-progress-wrap]')?.setAttribute('hidden', '');
      this.querySelector('[data-csg-top-up]')?.setAttribute('hidden', '');
    }
  }

  class CartShippingGuideManager extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.removeOrphanedInstances();
      this.template = this.querySelector('[data-csg-template]');
      this.instances = new Map();
      this.refresh = debounce(() => this.fetchCart(), 80);
      this.remount = debounce(() => this.mountAll(), 80);
      this.refreshAfterInteraction = debounce(() => {
        this.fetchCart();
        this.delayedRefreshTimer = window.setTimeout(() => this.fetchCart(), 700);
      }, 120);
      this.mountAll();
      this.bindEvents();
      this.observeCartRegions();
      this.refresh();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
      window.clearTimeout(this.delayedRefreshTimer);
      this.instances?.forEach((instance) => instance.remove());
      this.instances?.clear();
    }

    removeOrphanedInstances() {
      const owner = this.dataset.sectionId;
      if (!owner) return;
      document.querySelectorAll('cart-shipping-guide[data-csg-owner]').forEach((instance) => {
        if (instance.dataset.csgOwner === owner) instance.remove();
      });
    }

    copyConfiguration(instance) {
      Object.entries(this.dataset).forEach(([key, value]) => {
        if (key !== 'initialized') instance.dataset[key] = value;
      });
      instance.className = 'csg-instance';
      if (this.dataset.shadow === 'true') instance.classList.add('csg-instance--shadow');
      if (this.dataset.border === 'false') instance.classList.add('csg-instance--no-border');
    }

    createInstance(key, target, inline = false) {
      const existing = this.instances.get(key);
      if (existing && document.contains(existing)) return existing;
      const instance = document.createElement('cart-shipping-guide');
      this.copyConfiguration(instance);
      instance.dataset.location = key;
      instance.dataset.csgOwner = this.dataset.sectionId || 'default';
      instance.append(this.template.content.cloneNode(true));
      if (inline) this.append(instance);
      else target?.insertAdjacentElement(this.dataset.mountPosition || 'beforebegin', instance);
      this.instances.set(key, instance);
      return instance;
    }

    mountAll() {
      for (const [key, instance] of this.instances) {
        if (!document.contains(instance)) this.instances.delete(key);
      }
      const location = this.dataset.displayLocation || 'both';
      if (location === 'inline') {
        this.createInstance('inline', this, true);
        return;
      }
      const cartPath = (this.dataset.cartPageUrl || '/cart').replace(/\/$/, '');
      const currentPath = window.location.pathname.replace(/\/$/, '');
      if ((location === 'page' || location === 'both') && currentPath === cartPath) {
        const pageTarget = safeSelector(this.dataset.pageSelector);
        if (pageTarget) this.createInstance('page', pageTarget);
      }
      if (location === 'drawer' || location === 'both') {
        const drawerTarget = safeSelector(this.dataset.drawerSelector);
        if (drawerTarget) this.createInstance('drawer', drawerTarget);
      }
      if (this.lastCart) this.updateInstances(this.lastCart);
    }

    bindEvents() {
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      CART_EVENTS.forEach((eventName) => document.addEventListener(eventName, this.refresh, options));
      document.addEventListener('shopify:section:load', this.remount, options);
      document.addEventListener('shopify:section:reorder', this.remount, options);
      document.addEventListener('change', (event) => {
        if (this.isCartRegion(event.target)) this.refreshAfterInteraction();
      }, options);
      document.addEventListener('submit', (event) => {
        if (this.isCartRegion(event.target)) this.refreshAfterInteraction();
      }, options);
    }

    isCartRegion(node) {
      const element = node instanceof Element ? node : node?.parentElement;
      if (!element || element.closest('cart-shipping-guide-manager, cart-shipping-guide')) return false;
      return Boolean(element.closest(
        'cart-drawer, #CartDrawer, .cart-drawer, [data-cart-drawer], #main-cart-items, #main-cart-footer, [data-cart-items], [data-cart-footer], form[action*="/cart"]'
      ));
    }

    observeCartRegions() {
      if (!document.body || !window.MutationObserver) return;
      this.observer = new MutationObserver((mutations) => {
        let cartChanged = false;
        for (const mutation of mutations) {
          if (this.isCartRegion(mutation.target)) {
            cartChanged = true;
            break;
          }
          for (const node of mutation.addedNodes) {
            if (!(node instanceof Element)) continue;
            if (node.closest('cart-shipping-guide-manager, cart-shipping-guide')) continue;
            const identity = `${node.id} ${node.className || ''} ${node.tagName}`.toLowerCase();
            if (identity.includes('cart') || node.querySelector?.('cart-drawer, [data-cart-drawer], #main-cart-items, [data-cart-items]')) {
              cartChanged = true;
              break;
            }
          }
          if (cartChanged) break;
        }
        if (cartChanged) {
          this.remount();
          this.refresh();
        }
      });
      this.observer.observe(document.body, { childList: true, characterData: true, subtree: true });
    }

    async fetchCart() {
      try {
        const response = await fetch(this.dataset.cartUrl || '/cart.js', {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store'
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        this.lastCart = await response.json();
        this.mountAll();
        this.updateInstances(this.lastCart);
      } catch (error) {
        console.warn('Cart Shipping Guide: cart data request failed.', error);
        this.instances.forEach((instance) => instance.showFallback());
      }
    }

    updateInstances(cart) {
      this.instances.forEach((instance) => instance.update(cart));
    }
  }

  if (!customElements.get('cart-shipping-guide')) customElements.define('cart-shipping-guide', CartShippingGuide);
  if (!customElements.get('cart-shipping-guide-manager')) customElements.define('cart-shipping-guide-manager', CartShippingGuideManager);

  if (!window.__cartShippingGuideNetworkHook) {
    window.__cartShippingGuideNetworkHook = true;
    const mutationPattern = /\/(?:cart\/(?:add|change|update|clear)(?:\.js)?|discount(?:\/|\?|$))/;
    const notify = () => document.dispatchEvent(new CustomEvent(REFRESH_EVENT));

    const originalFetch = window.fetch;
    window.fetch = async function (...args) {
      const response = await originalFetch.apply(this, args);
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      if (response.ok && mutationPattern.test(url)) window.setTimeout(notify, 0);
      return response;
    };

    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__csgCartMutation = mutationPattern.test(String(url || ''));
      return originalOpen.call(this, method, url, ...rest);
    };
    XMLHttpRequest.prototype.send = function (...args) {
      if (this.__csgCartMutation) this.addEventListener('loadend', notify, { once: true });
      return originalSend.apply(this, args);
    };
  }
})();
